import { notify } from 'libs/common/notify'

import { getElopageConfig } from 'utils/elopageConfig.utils'
import { isProduction } from 'utils/env.utils'

import {
  Stripe,
  StripeElements,
  StripeCardElement,
  StripeCardNumberElement,
  StripeExpressCheckoutElement,
  StripeP24BankElement,
  StripeElementsOptionsMode,
  PaymentIntentResult,
  SetupIntentResult,
  StripeMessageParams,
  KlarnaFormSettings,
} from 'shop/types/stripe'

import { PAYMENT_FORMS } from 'constants/paymentSettingShared.constants'
import { hasWindow } from './browser.utils'

type HandleStripe = Promise<PaymentIntentResult> | undefined
type IntentResult = PaymentIntentResult | SetupIntentResult

export const getStripeStyles = (paymentForm = '') => {
  switch (paymentForm) {
    case PAYMENT_FORMS.p24:
    case PAYMENT_FORMS.ideal:
    case PAYMENT_FORMS.klarna:
      return {
        base: {
          backgroundColor: '#fff',
          padding: '15px 13px',
          color: '#4a4a4a',
          lineHeight: '16px',
          fontFamily: 'Montserrat Reg, sans-serif',
          fontSmoothing: 'antialiased',
          fontSize: '13px',
          '::placeholder': {
            color: '#4a4a4a',
          },
          ':hover': {
            backgroundColor: '#E4F0FA',
          },
        },
        invalid: {
          color: '#fa755a',
          iconColor: '#fa755a',
        },
      }

    case PAYMENT_FORMS.card:
    default:
      return {
        base: {
          color: '#32325d',
          lineHeight: '18px',
          fontFamily: 'Nunito Sans, sans-serif',
          fontSmoothing: 'antialiased',
          fontSize: '16px',
          '::placeholder': {
            color: '#aab7c4',
          },
        },
        invalid: {
          color: '#fa755a',
          iconColor: '#fa755a',
        },
      }
  }
}

export const getNewStripeStyles = (paymentForm = '') => {
  switch (paymentForm) {
    case PAYMENT_FORMS.p24:
    case PAYMENT_FORMS.ideal:
    case PAYMENT_FORMS.klarna:
      return {
        base: {
          backgroundColor: '#fff',
          padding: '16px',
          fontFamily: 'Inter, sans-serif',
          fontSize: '14px',
          fontWeight: 400,

          ':hover': {
            backgroundColor: '#f3f5f8',
          },
        },
        invalid: {
          color: '#fa755a',
          iconColor: '#fa755a',
        },
      }

    case PAYMENT_FORMS.card:
    default:
      return {
        base: {
          color: '#000004',
          fontFamily: 'Inter, sans-serif',
          fontSize: '14px',
          iconColor: '#485056',

          '::placeholder': {
            color: '#8f9295',
          },
        },
        invalid: {
          color: '#fa755a',
          iconColor: '#fa755a',
        },
      }
  }
}

export const isStripeInjected = () => hasWindow && window.Stripe

export const injectStripeScript = (onLoadHandler = () => {}, injected = () => {}): void => {
  /* Do nothing in case it's already injected */
  if (isStripeInjected()) {
    injected()
    return
  }

  const script = document.createElement('script')
  script.type = 'text/javascript'
  script.src = 'https://js.stripe.com/v3/'
  document.body.appendChild(script)

  script.onload = () => {
    onLoadHandler()
  }
}

export const createStripeClient = (stripePubKey = '') => {
  if (hasWindow && (window as any).Stripe) {
    return (window as any).Stripe(stripePubKey, { betas: ['klarna_pm_beta_1'] })
  }
}

export const createStripeElements = (
  stripeClient?: {
    elements: (localeObj: { locale: string }) => object
  },
  params?: StripeElementsOptionsMode | { clientSecret: string } | StripeMessageParams
): object | void => {
  if (stripeClient) {
    return stripeClient.elements({
      locale: I18n.locale,
      ...(params ? params : {}),
    })
  }
}

export const createStripeP24 = (stripeElements?: {
  create: (provider: string, style: object) => object
}): object | void => {
  if (stripeElements) {
    return stripeElements.create('p24Bank', {
      style: getNewStripeStyles(PAYMENT_FORMS.p24),
    })
  }
}

export const createStripeCard = (stripeElements?: {
  create: (card: string, style: object) => object
}): object | void => {
  if (stripeElements) {
    return stripeElements.create('card', {
      style: getNewStripeStyles(PAYMENT_FORMS.card),
    })
  }
}

export const createStripeKlarna = (
  stripeElements: {
    create: (provider: string, options: KlarnaFormSettings) => object
  },
  options: KlarnaFormSettings
): object | void => {
  if (stripeElements) {
    return stripeElements.create('payment', {
      ...options,
    })
  }
}

export const createStripeIdeal = (
  stripeElements?: {
    create: (provider: string, style: object) => object
  },
  options?
): object | void => {
  if (stripeElements) {
    return stripeElements.create('payment', {
      fields: {
        billingDetails: 'never',
      },
      ...options,
    })
  }
}

export const createStripeTwint = (
  stripeElements?: {
    create: (type: string, style: object) => object
  },
  options?
) => {
  if (stripeElements) {
    return stripeElements.create('payment', options)
  }
}

export const createStripeMondu = (
  stripeElements?: {
    create: (type: string, style: object) => object
  },
  options?
) => {
  if (stripeElements) {
    return stripeElements.create('payment', options)
  }
}

export const createStripePayByBank = (
  stripeElements?: {
    create: (type: string, style: object) => object
  },
  options?
) => {
  if (stripeElements) {
    return stripeElements.create('payment', options)
  }
}

export const createStripeSepa = (
  stripeElements?: {
    create: (card: string, style: object) => object
  },
  options?
) => {
  if (stripeElements) {
    return stripeElements.create('payment', options)
  }
}

export const handleSofortTransaction = (
  stripeClient?: Stripe,
  redirectLink?: string,
  clientSecret?: string,
  payer = {
    countryCode: '',
    billingDetails: {},
  }
): HandleStripe => {
  if (stripeClient && clientSecret) {
    return stripeClient.confirmSofortPayment(clientSecret, {
      payment_method: {
        sofort: {
          country: (payer.countryCode || '').toUpperCase(),
        },
        ...payer.billingDetails,
      },
      return_url: redirectLink,
    })
  }
}

export const handleSepaError = (error) => {
  const messageContainer = document.querySelector('#sepa-error-message')
  messageContainer.textContent = error.message
}

export const handlePayByBankError = (error) => {
  const messageContainer = document.querySelector('#pay-by-bank-error-message')
  if (messageContainer) {
    messageContainer.textContent = error.message
  }
}

export const handleP24Transaction = (
  stripeClient?: Stripe,
  stripeP24?: StripeP24BankElement,
  redirectLink?: string,
  clientSecret?: string,
  payer = {
    fullName: '',
    email: '',
    tosShowAndAccepted: false,
  }
): HandleStripe => {
  if (stripeClient && clientSecret && stripeP24) {
    return stripeClient.confirmP24Payment(clientSecret, {
      payment_method: {
        p24: stripeP24,
        billing_details: {
          name: payer.fullName,
          email: payer.email,
        },
      },
      payment_method_options: {
        p24: {
          // In order to be able to pass the `tos_shown_and_accepted` parameter, you must
          // ensure that the P24 regulations and information obligation consent
          // text is clearly visible to the customer. See
          // https://stripe.com/docs/payments/p24/accept-a-payment#requirements
          // for directions.
          tos_shown_and_accepted: payer.tosShowAndAccepted,
        },
      },
      return_url: redirectLink,
    })
  }
}

export const handleIdealTransaction = (
  stripeClient?: Stripe,
  redirectLink?: string,
  clientSecret?: string,
  payer = {
    fullName: '',
    email: '',
  }
): HandleStripe => {
  if (stripeClient && clientSecret) {
    return stripeClient.confirmIdealPayment(clientSecret, {
      payment_method: {
        ideal: {},
        billing_details: {
          name: payer.fullName,
          email: payer.email,
        },
      },
      return_url: redirectLink,
    })
  }
}

export const handlePayByBankTransaction = (
  stripeElement: StripeElements,
  stripeClient: Stripe,
  paymentMethodUserData: { redirectLink: string; id?: string; billingDetails?: object },
  clientSecret?: string
): HandleStripe => {
  if (stripeClient && clientSecret) {
    return stripeClient.confirmPayment({
      elements: stripeElement,
      clientSecret: clientSecret,
      confirmParams: {
        ...(paymentMethodUserData.id
          ? {
              payment_method: paymentMethodUserData?.id,
            }
          : {
              payment_method_data: {
                billing_details: paymentMethodUserData.billingDetails,
              },
            }),
        return_url: paymentMethodUserData.redirectLink,
      },
      redirect: 'if_required',
    })
  }
}

export const getKlarnaFormSettings = (): KlarnaFormSettings => ({
  fields: {
    billingDetails: 'never',
  },
})

export const handleStripeCardTransaction = (
  stripeClient: Stripe,
  paymentMethodUserData: { id?: string; stripeCard?: StripeCardElement | StripeCardNumberElement },
  clientSecret: string,
  userData?: {
    fullName?: string
    email?: string
    tosShowAndAccepted?: boolean
  },
  setupIntentSecret?: string
): Promise<PaymentIntentResult> | Promise<SetupIntentResult> => {
  if (stripeClient && (clientSecret || setupIntentSecret)) {
    const billingDetails: { name?: string; email?: string } = {}
    if (userData?.fullName) {
      billingDetails.name = userData?.fullName
    }
    if (userData?.email) {
      billingDetails.email = userData?.email
    }

    const paymentMethodData = paymentMethodUserData.id || {
      card: paymentMethodUserData.stripeCard,
      billing_details: billingDetails,
    }

    if (setupIntentSecret) {
      return stripeClient.confirmCardSetup(setupIntentSecret, {
        payment_method: paymentMethodData,
      })
    }

    return stripeClient.confirmCardPayment(clientSecret, {
      payment_method: paymentMethodData,
    })
  }
}

export const handleTransactionResponse = (
  result: IntentResult,
  handleSuccess: () => void,
  handleError: () => void,
  setupIntentSecret?: string
) => {
  const { error } = result
  const intent = setupIntentSecret
    ? (result as SetupIntentResult).setupIntent
    : (result as PaymentIntentResult).paymentIntent

  if (intent) {
    handleSuccess()
  } else if (error.message) {
    notify('error', error.message)
    handleError()
  }
}

export const handleAdditionalConfirmation = (
  stripeClient: Stripe,
  handleSuccess: () => void,
  handleError: () => void,
  clientSecret: string,
  paymentMethodUserData?: { id?: string; stripeCard?: StripeCardElement | StripeCardNumberElement },
  billingDetails?: {
    fullName?: string
    email?: string
  },
  pubKey?: string
) => {
  const handleTransaction = async () => {
    const client = stripeClient || createStripeClient(pubKey)
    const resp = await client.retrievePaymentIntent(clientSecret)

    if (resp?.paymentIntent?.status?.includes('requires')) {
      handleStripeCardTransaction(
        client,
        {
          id: paymentMethodUserData.id,
          stripeCard: paymentMethodUserData.stripeCard,
        },
        clientSecret,
        billingDetails
      ).then((result: IntentResult) => handleTransactionResponse(result, handleSuccess, handleError))
    } else {
      handleSuccess()
    }
  }

  if (!isStripeInjected()) {
    injectStripeScript(handleTransaction)
  } else {
    handleTransaction()
  }
}

export const handleStripeCardRecuringPayment = (
  clientSecret: string,
  paymentMethod: string,
  stripePubKey: string,
  handleSuccess: () => void,
  handleError: () => void
): void => {
  const handleTransaction = (): void => {
    const stripeClient = createStripeClient(stripePubKey)
    handleStripeCardTransaction(stripeClient, { id: paymentMethod }, clientSecret, {}).then((result: IntentResult) =>
      handleTransactionResponse(result, handleSuccess, handleError)
    )
  }

  if (!isStripeInjected()) {
    injectStripeScript(handleTransaction)
  } else {
    handleTransaction()
  }
}

export const handleStripeSofortRecuringPayment = (
  clientSecret: string,
  stripePubKey: string,
  redirectLink: string,
  payerInfo: { countryCode: string; billingDetails: object } | undefined,
  handleSuccess: () => void,
  handleError: () => void
): void => {
  const handleTransaction = (): void => {
    const stripeClient = createStripeClient(stripePubKey)
    handleSofortTransaction(stripeClient, redirectLink, clientSecret, payerInfo).then((result: PaymentIntentResult) =>
      handleTransactionResponse(result, handleSuccess, handleError)
    )
  }

  if (!isStripeInjected()) {
    injectStripeScript(handleTransaction)
  } else {
    handleTransaction()
  }
}

export const handleStripeDigitalTransaction = (
  stripeClient: Stripe,
  expressCheckoutElement: StripeExpressCheckoutElement,
  elements: StripeElements,
  clientSecret: string,
  redirectLink: string,
  handleClose: () => void
) => {
  if (stripeClient && clientSecret) {
    expressCheckoutElement.on('confirm', async () => {
      const { error } = await stripeClient.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: redirectLink,
        },
      })

      if (error) {
        notify('error', error.message)

        if (handleClose) {
          handleClose()
        }
      }
    })
  }
}

export const convertPriceForStripe = (price: number): number => Math.round((Number(price) || 0) * 100)

export const allowedToUseStripe = () => {
  const webProtocol = getElopageConfig('webProtocol')
  const isHttp = webProtocol === 'http'
  return !(isProduction() && isHttp)
}
